import type { AxiosInstance } from "axios";

import { handleError } from "@/utils";

/**
 * Polls an endpoint at a specified interval until a success or failure response is received.
 *
 * @template T The expected response data type.
 * @param {AxiosInstance} axiosInstance - The Axios instance to use for the request.
 * @param {string} endpoint - The endpoint to poll.
 * @param {string} [errorMessage] - An optional custom error message to use when a failure response is received.
 * @param {number} [interval=2000] - The interval in milliseconds between each poll. Default is 2000ms.
 *
 * @returns {Promise<T>} A promise that resolves to the response data when a success or failure response is received.
 * @throws Will throw an error if the request fails with a status other than 404.
 */
export const pollEndpoint = async <T>(
    axiosInstance: AxiosInstance,
    endpoint: string,
    errorMessage?: string,
    interval: number = 2000
): Promise<T> => {
    try {
        const response = await axiosInstance.get(endpoint);

        if (response.data.status === "FAILURE") {
            handleError(errorMessage || response.data.result, true);
            return response.data as T;
        }

        if (["SUCCESS", "FAILURE"].includes(response.data.status)) {
            return response.data as T;
        }

        return pollEndpoint(axiosInstance, endpoint, errorMessage, interval);
    } catch (error: any) {
        if (error.response && error.response.status === 404) {
            return pollEndpoint(
                axiosInstance,
                endpoint,
                errorMessage,
                interval
            );
        }

        throw error;
    }
};
